<template>
  <div class="nav-actions">
    <!-- Language and Currency Dropdowns -->
    <div class="dropdown-container">
      <!-- Language Dropdown (disabled for now) -->

      <!-- Currency Dropdown -->
      <div class="dropdown currency-dropdown" @click.stop="toggleCurrencyDropdown">
        <div class="dropdown-trigger">
          <span class="currency-symbol">{{
            currentCurrencyConfig?.symbol || preferredCurrency
          }}</span>
          <span class="currency-code">{{ currentCurrencyConfig?.name || preferredCurrency }}</span>
          <svg
            class="dropdown-arrow"
            :class="{ rotated: showCurrencyDropdown }"
            width="12"
            height="12"
            viewBox="0 0 24 24"
          >
            <path d="M7 10l5 5 5-5z" fill="currentColor" />
          </svg>
        </div>
        <div class="dropdown-menu currency-grid" v-if="showCurrencyDropdown" @click.stop>
          <div
            v-for="rate in availableCurrencies"
            :key="rate.name"
            class="currency-item"
            :class="{ active: preferredCurrency === rate.name }"
            @click="switchCurrency(rate.name)"
          >
            <div class="currency-preview">
              <span class="currency-flag">{{ rate.icon }}</span>
              <span class="currency-name">{{ rate.name }}</span>
            </div>
            <div class="currency-rate">
              {{ getCurrencyPreview(rate) }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Show user info when logged in -->
    <div v-if="authStore.isAuthenticated" class="user-section" ref="userSectionRef">
      <div class="user-info" @click="showUserMenu = !showUserMenu">
        <span class="user-avatar">{{
          (sanitizedUserName || sanitizedUserUsername || 'U').charAt(0).toUpperCase()
        }}</span>
      </div>

      <!-- User dropdown menu -->
      <div v-if="showUserMenu" class="user-menu">
        <div class="user-menu-header">
          <strong>{{ sanitizedUserName || sanitizedUserUsername || 'User' }}</strong>
          <span class="user-email">{{ authStore.user?.email }}</span>
        </div>
        <div class="user-menu-actions">
          <router-link to="/tf" class="menu-item" @click="showUserMenu = false">
            My TakeFavs
          </router-link>
          <router-link to="/profile" class="menu-item" @click="showUserMenu = false">
            My Profile
          </router-link>
          <router-link
            v-if="authStore.user?.role === 'admin' || authStore.user?.role === 'editor'"
            to="/crm"
            class="menu-item"
            @click="showUserMenu = false"
          >
            CRM Dashboard
          </router-link>
          <button @click="logout" class="menu-item logout-btn">Sign Out</button>
        </div>
      </div>
    </div>

    <!-- Show auth links when not logged in -->
    <template v-else>
      <router-link to="/sign-in" class="auth-link">Sign In</router-link>
      <router-link to="/sign-up" class="auth-link sign-up">Sign Up</router-link>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '../stores/authStore'
import { useCurrency } from '../composables/useCurrency'
import { sanitizeNameDisplay, sanitizeUsernameDisplay } from '@/utils/sanitize'

defineOptions({ name: 'NavActions' })

const router = useRouter()
const authStore = useAuthStore()
const { preferredCurrency, availableCurrencies, setPreferredCurrency, currentCurrencyInfo } =
  useCurrency()

const showUserMenu = ref(false)
const userSectionRef = ref<HTMLElement>()

// Dropdown states
const showLanguageDropdown = ref(false)
const showCurrencyDropdown = ref(false)

// Currency configuration
const currentCurrencyConfig = computed(() => currentCurrencyInfo.value)

// Sanitized user data for safe display
const sanitizedUserName = computed(() =>
  sanitizeNameDisplay(authStore.user?.name)
)
const sanitizedUserUsername = computed(() =>
  sanitizeUsernameDisplay(authStore.user?.username)
)

const logout = () => {
  authStore.logout()
  router.push('/')
  showUserMenu.value = false
}

// Dropdown toggle functions
const toggleCurrencyDropdown = () => {
  showCurrencyDropdown.value = !showCurrencyDropdown.value
  showLanguageDropdown.value = false
}

// Currency switching
const switchCurrency = (currency: string) => {
  setPreferredCurrency(currency)
  showCurrencyDropdown.value = false
}

// Get currency preview with dynamic base conversion
const getCurrencyPreview = (rate: { name: string; value: number; symbol: string }): string => {
  if (rate.name === preferredCurrency.value) {
    return `${rate.symbol} 1.000 (base)`
  }

  // Convert from current base to this currency
  const currentBaseRate = availableCurrencies.value.find((r) => r.name === preferredCurrency.value)
  if (currentBaseRate) {
    const convertedRate = rate.value / currentBaseRate.value
    return `${rate.symbol} ${convertedRate.toFixed(3)}`
  }

  return `${rate.symbol} ${rate.value.toFixed(3)}`
}

// Handle click outside to close dropdowns
const handleClickOutside = (event: Event) => {
  const target = event.target as HTMLElement

  // Close user menu if clicking outside
  if (userSectionRef.value && !userSectionRef.value.contains(target)) {
    showUserMenu.value = false
  }

  // Close currency dropdown if clicking outside
  if (!target.closest('.currency-dropdown')) {
    showCurrencyDropdown.value = false
  }

  // Close language dropdown if clicking outside
  if (!target.closest('.language-dropdown')) {
    showLanguageDropdown.value = false
  }
}

onMounted(() => {
  // Add click outside listener
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  // Remove click outside listener
  document.removeEventListener('click', handleClickOutside)
})
</script>

<style lang="scss" scoped>
.nav-actions {
  display: flex;
  align-items: center;
  gap: 1rem;
}

/* Dropdown Container */
.dropdown-container {
  display: flex;
  gap: 0.5rem;
}

/* Dropdown Styles */
.dropdown {
  position: relative;
  cursor: pointer;
}

.dropdown-arrow {
  transition: transform 0.2s;

  &.rotated {
    transform: rotate(180deg);
  }
}

.dropdown-menu {
  position: absolute;
  top: -4px;
  right: 10px;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  min-width: 200px;
  z-index: 1000;
  margin-top: 0.25rem;
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f5f5f5;
  }

  &.active {
    background-color: #e3f2fd;
    color: #1976d2;
  }
}

/* Language Dropdown */
.language-dropdown .dropdown-trigger {
  .flag {
    font-size: 1.2rem;
  }

  .language-name {
    font-weight: 500;
    font-size: 0.875rem;
  }
}

/* Currency Dropdown */
.currency-dropdown .dropdown-trigger {
  .currency-symbol {
    font-weight: bold;
    font-size: 1rem;
  }

  .currency-code {
    font-size: 0.875rem;
    color: #666;
  }
  @media (max-width: 768px) {
    * {
      font-size: 1.4rem;
    }
  }
}

/* Currency Grid */
.currency-grid {
  display: grid;
  gap: 0.5rem;
  padding: 1rem;
  min-width: 300px;
  @media (max-width: 768px) {
    right: calc(50% - 160px);
  }
}

.currency-item {
  display: flex;
  justify-content: space-around;
  align-items: center;
  padding: 0.75rem;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: white;
  > div {
    width: 38%;
  }

  &:hover {
    background-color: #f8f9fa;
    border-color: #007bff;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 123, 255, 0.15);
  }

  &.active {
    background-color: #e3f2fd;
    border-color: #1976d2;
    box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.2);
  }
}

.currency-preview {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.25rem;

  .currency-flag {
    font-size: 1.25rem;
  }

  .currency-name {
    font-weight: 600;
    font-size: 0.875rem;
    color: #333;
  }
}

.currency-rate {
  font-size: 0.75rem;
  color: #666;
  font-weight: 500;
  text-align: center;
}

/* User Section */
.user-section {
  position: relative;
}

.user-info {
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 50%;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f5f5f5;
  }
}

.user-avatar {
  width: 32px;
  height: 32px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 0.875rem;
}

.user-menu {
  position: absolute;
  top: 100%;
  right: 0;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  min-width: 250px;
  z-index: 1000;
  margin-top: 0.5rem;
}

.user-menu-header {
  padding: 1rem;
  border-bottom: 1px solid #e0e0e0;

  strong {
    display: block;
    font-size: 1rem;
    margin-bottom: 0.25rem;
  }

  .user-email {
    font-size: 0.875rem;
    color: #666;
  }
}

.user-menu-actions {
  padding: 0.5rem 0;
}

.menu-item {
  display: block;
  width: 100%;
  padding: 0.75rem 1rem;
  text-decoration: none;
  color: #333;
  transition: background-color 0.2s;
  border: none;
  background: none;
  font-size: 0.875rem;
  text-align: left;
  cursor: pointer;

  &:hover {
    background-color: #f5f5f5;
  }

  &.logout-btn {
    color: #d32f2f;

    &:hover {
      background-color: #ffebee;
    }
  }
}

/* Auth Links */
.auth-link {
  text-decoration: none;
  color: #666;
  font-weight: 500;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  transition: all 0.2s;

  &:hover {
    color: #667eea;
    background-color: #f5f5f5;
  }

  &.sign-up {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
    }
  }
}

/* Responsive Design */
@media (max-width: 768px) {
  .nav-actions {
    gap: 0.5rem;
  }

  .dropdown-container {
    gap: 0.25rem;
  }

  .auth-link {
    padding: 0.2rem 0.375rem;
    font-size: 0.875rem;
  }

  .user-avatar {
    width: 28px;
    height: 28px;
    font-size: 0.75rem;
  }
}
</style>
